import { BufferAttribute, BufferGeometry, Color, IcosahedronGeometry, Vector3 } from 'three';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { Rng } from '@cloudpearl/core';

export type RGB = [number, number, number];

const _c = new Color();
/** sRGB hex → linear RGB with optional value/hue jitter. */
export function rgb(hex: string, rng?: Rng, jitter = 0): RGB {
  _c.set(hex);
  if (rng && jitter > 0) {
    const k = 1 + (rng.float() - 0.5) * 2 * jitter;
    const hs = { h: 0, s: 0, l: 0 };
    _c.getHSL(hs);
    _c.setHSL(hs.h + (rng.float() - 0.5) * jitter * 0.08, hs.s, Math.min(1, hs.l * k));
  }
  return [_c.r, _c.g, _c.b];
}

export const mixRGB = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
export const scaleRGB = (a: RGB, k: number): RGB => [a[0] * k, a[1] * k, a[2] * k];

/** Cheap 3D value noise for shaping (render-only, so Math.sin is fine). */
export function noise3(x: number, y: number, z: number, seed = 0): number {
  const h = (ix: number, iy: number, iz: number): number => {
    const s = Math.sin(ix * 127.1 + iy * 311.7 + iz * 74.7 + seed * 19.19) * 43758.5453;
    return s - Math.floor(s);
  };
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const iz = Math.floor(z);
  const fx = x - ix;
  const fy = y - iy;
  const fz = z - iz;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const uz = fz * fz * (3 - 2 * fz);
  const l = (a: number, b: number, t: number): number => a + (b - a) * t;
  return l(
    l(l(h(ix, iy, iz), h(ix + 1, iy, iz), ux), l(h(ix, iy + 1, iz), h(ix + 1, iy + 1, iz), ux), uy),
    l(l(h(ix, iy, iz + 1), h(ix + 1, iy, iz + 1), ux), l(h(ix, iy + 1, iz + 1), h(ix + 1, iy + 1, iz + 1), ux), uy),
    uz,
  );
}

export function fbm3(x: number, y: number, z: number, seed = 0, octaves = 4): number {
  let s = 0;
  let a = 0.5;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    s += a * noise3(x, y, z, seed + i * 7.3);
    norm += a;
    x = x * 2.03 + 1.7;
    y = y * 2.03 - 3.1;
    z = z * 2.03 + 5.3;
    a *= 0.5;
  }
  return s / norm;
}

/**
 * Indexed mesh accumulator: position, normal, color, uv (+ optional scalar extra). Smooth
 * surfaces share vertices; hard-edged parts (boxes) add their own.
 */
export class MeshBuilder {
  readonly pos: number[] = [];
  readonly nor: number[] = [];
  readonly col: number[] = [];
  readonly uv: number[] = [];
  readonly extra: number[] = [];
  readonly idx: number[] = [];

  get vertexCount(): number {
    return this.pos.length / 3;
  }

  get empty(): boolean {
    return this.idx.length === 0;
  }

  vertex(p: Vector3, n: Vector3, c: RGB, u = 0, v = 0, e = 0): number {
    this.pos.push(p.x, p.y, p.z);
    this.nor.push(n.x, n.y, n.z);
    this.col.push(c[0], c[1], c[2]);
    this.uv.push(u, v);
    this.extra.push(e);
    return this.pos.length / 3 - 1;
  }

  tri(a: number, b: number, c: number): void {
    this.idx.push(a, b, c);
  }

  /** Flat quad (own vertices, face normal). Winding a→b→c→d counter-clockwise from the front. */
  flatQuad(a: Vector3, b: Vector3, c: Vector3, d: Vector3, color: RGB, uvScale = 1): void {
    const n = new Vector3().subVectors(b, a).cross(new Vector3().subVectors(d, a)).normalize();
    const ab = a.distanceTo(b) * uvScale;
    const ad = a.distanceTo(d) * uvScale;
    const i = this.vertex(a, n, color, 0, 0);
    this.vertex(b, n, color, ab, 0);
    this.vertex(c, n, color, ab, ad);
    this.vertex(d, n, color, 0, ad);
    this.idx.push(i, i + 1, i + 2, i, i + 2, i + 3);
  }

  /** Append an indexed or non-indexed geometry (position + normal, optional color/uv). */
  addGeometry(g: BufferGeometry, color?: RGB, extra = 0): void {
    const base = this.vertexCount;
    const p = g.getAttribute('position');
    const n = g.getAttribute('normal');
    const c = g.getAttribute('color');
    const uv = g.getAttribute('uv');
    for (let i = 0; i < p.count; i++) {
      this.pos.push(p.getX(i), p.getY(i), p.getZ(i));
      this.nor.push(n.getX(i), n.getY(i), n.getZ(i));
      if (c) this.col.push(c.getX(i), c.getY(i), c.getZ(i));
      else this.col.push(...(color ?? [1, 1, 1]));
      if (uv) this.uv.push(uv.getX(i), uv.getY(i));
      else this.uv.push(0, 0);
      this.extra.push(extra);
    }
    if (g.index) {
      const ia = g.index.array;
      for (let i = 0; i < ia.length; i++) this.idx.push(ia[i]! + base);
    } else {
      for (let i = 0; i < p.count; i++) this.idx.push(base + i);
    }
  }

  merge(o: MeshBuilder): void {
    const base = this.vertexCount;
    this.pos.push(...o.pos);
    this.nor.push(...o.nor);
    this.col.push(...o.col);
    this.uv.push(...o.uv);
    this.extra.push(...o.extra);
    for (const i of o.idx) this.idx.push(i + base);
  }

  build(extraName?: string): BufferGeometry {
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(new Float32Array(this.pos), 3));
    g.setAttribute('normal', new BufferAttribute(new Float32Array(this.nor), 3));
    g.setAttribute('color', new BufferAttribute(new Float32Array(this.col), 3));
    g.setAttribute('uv', new BufferAttribute(new Float32Array(this.uv), 2));
    if (extraName) g.setAttribute(extraName, new BufferAttribute(new Float32Array(this.extra), 1));
    const count = this.vertexCount;
    g.setIndex(new BufferAttribute(count > 65535 ? new Uint32Array(this.idx) : new Uint16Array(this.idx), 1));
    g.computeBoundingSphere();
    g.computeBoundingBox();
    return g;
  }
}

/** Box with hard edges and per-face colour jitter (buildings, lintels). */
export function box(mb: MeshBuilder, cx: number, cy: number, cz: number, sx: number, sy: number, sz: number, color: RGB, rng?: Rng, jitter = 0): void {
  const j = (): number => (rng ? (rng.float() - 0.5) * jitter : 0);
  const x0 = cx - sx / 2;
  const x1 = cx + sx / 2;
  const y0 = cy - sy / 2;
  const y1 = cy + sy / 2;
  const z0 = cz - sz / 2;
  const z1 = cz + sz / 2;
  const v = [
    new Vector3(x0 + j(), y0, z0 + j()),
    new Vector3(x1 + j(), y0, z0 + j()),
    new Vector3(x1 + j(), y1 + j(), z0 + j()),
    new Vector3(x0 + j(), y1 + j(), z0 + j()),
    new Vector3(x0 + j(), y0, z1 + j()),
    new Vector3(x1 + j(), y0, z1 + j()),
    new Vector3(x1 + j(), y1 + j(), z1 + j()),
    new Vector3(x0 + j(), y1 + j(), z1 + j()),
  ];
  const f = (a: number, b: number, c: number, d: number): void => {
    const k = rng ? 0.95 + rng.float() * 0.1 : 1;
    mb.flatQuad(v[a]!, v[b]!, v[c]!, v[d]!, scaleRGB(color, k));
  };
  f(4, 5, 6, 7); // +z
  f(1, 0, 3, 2); // −z
  f(5, 1, 2, 6); // +x
  f(0, 4, 7, 3); // −x
  f(3, 7, 6, 2); // top
  f(0, 1, 5, 4); // bottom
}

/**
 * Smooth boulder: a finely subdivided sphere, displaced by layered noise, with a few softened
 * planar cleaves so it still reads chiselled. Normalised so its horizontal radius is `radius` and
 * top is `height` (matching hazard colliders). Vertex colours carry large-scale tonal variation;
 * the triplanar normal map in the material does the fine detail.
 */
export function smoothRock(
  rng: Rng,
  opts: { radius: number; height: number; colors: RGB[]; detail?: number; roughness?: number; squash?: number; cleaves?: number; seed?: number },
): BufferGeometry {
  const ico = new IcosahedronGeometry(1, opts.detail ?? 6);
  ico.deleteAttribute('normal');
  ico.deleteAttribute('uv');
  const g = mergeVertices(ico);
  const p = g.getAttribute('position') as BufferAttribute;
  const seed = opts.seed ?? rng.float() * 100;
  const rough = opts.roughness ?? 0.2;
  const sx = 1 + (rng.float() - 0.5) * 0.4;
  const sz = 1 + (rng.float() - 0.5) * 0.4;
  const v = new Vector3();
  const cuts: { n: Vector3; d: number }[] = [];
  const nc = opts.cleaves ?? 4;
  for (let i = 0; i < nc; i++) {
    const n = new Vector3(rng.float() - 0.5, rng.float() * 0.9 - 0.15, rng.float() - 0.5).normalize();
    cuts.push({ n, d: rng.range(0.6, 0.84) });
  }
  for (let i = 0; i < p.count; i++) {
    v.fromBufferAttribute(p, i);
    const big = fbm3(v.x * 1.3 + seed, v.y * 1.3, v.z * 1.3, seed, 3) - 0.5;
    const ridge = 1 - Math.abs(noise3(v.x * 3.1 + seed, v.y * 3.1, v.z * 3.1, seed + 3) * 2 - 1);
    v.multiplyScalar(1 + big * rough * 2.2 + (ridge - 0.5) * rough * 0.35);
    for (const c of cuts) {
      const d = v.dot(c.n);
      // Soft clamp: flattened faces with rounded edges.
      if (d > c.d - 0.08) {
        const t = d - (c.d - 0.08);
        const soft = c.d - 0.08 + (0.08 * t) / (0.08 + t) * 1.6;
        v.addScaledVector(c.n, Math.min(soft, d) - d);
      }
    }
    v.x *= sx;
    v.z *= sz;
    v.y *= opts.squash ?? 0.85;
    if (v.y < -0.3) v.y = -0.3 + (v.y + 0.3) * 0.15;
    p.setXYZ(i, v.x, v.y, v.z);
  }
  g.computeBoundingBox();
  const bb = g.boundingBox!;
  const rH = Math.max(-bb.min.x, bb.max.x, -bb.min.z, bb.max.z);
  const scaleH = opts.radius / rH;
  const spanY = bb.max.y - bb.min.y;
  const colors = new Float32Array(p.count * 3);
  for (let i = 0; i < p.count; i++) {
    const y01 = (p.getY(i) - bb.min.y) / spanY;
    const y = y01 * 1.12 * opts.height - 0.12 * opts.height;
    const x = p.getX(i) * scaleH;
    const z = p.getZ(i) * scaleH;
    p.setXYZ(i, x, y, z);
    const t = noise3(x * 0.9 + seed, y * 0.9, z * 0.9, seed + 11);
    const ci = Math.min(opts.colors.length - 1, Math.floor(t * opts.colors.length));
    const a = opts.colors[ci]!;
    const b = opts.colors[(ci + 1) % opts.colors.length]!;
    const c = mixRGB(a, b, (t * opts.colors.length) % 1 * 0.5);
    // Darker toward the ground (soil-stained, occluded).
    const k = 0.62 + 0.38 * Math.min(1, y01 * 1.6 + 0.1);
    colors[i * 3] = c[0] * k;
    colors[i * 3 + 1] = c[1] * k;
    colors[i * 3 + 2] = c[2] * k;
  }
  g.setAttribute('color', new BufferAttribute(colors, 3));
  // Triplanar materials don't need UVs, but the attribute keeps layouts uniform for merging.
  g.setAttribute('uv', new BufferAttribute(new Float32Array(p.count * 2), 2));
  g.computeVertexNormals();
  g.computeBoundingSphere();
  return g;
}

export interface TubeRing {
  p: Vector3;
  r: number;
}

export interface TubeOptions {
  capEnd?: boolean;
  capStart?: boolean;
  /** Radial noise amount (fraction of radius). */
  jitter?: number;
  /** Vertical ribs (cactus): count and depth (fraction of radius). */
  ribs?: number;
  ribDepth?: number;
  /** Colour at the start / end of the tube (lerped), or a single colour. */
  color: RGB;
  colorEnd?: RGB;
  /** Darken toward the start (root AO). */
  rootAO?: number;
  /** End-cap dome height as a fraction of the end radius (default 0.35). */
  capLift?: number;
  seed?: number;
  /** Texel density around the tube: UV units per metre of circumference. */
  uvDensity?: number;
}

const _up = new Vector3(0, 1, 0);

/**
 * Smooth tube through rings with parallel-transported frames (no twisting), analytic normals
 * (radius taper included) and wrapped UVs: u around (seam duplicated), v along the length in
 * metres — so bark normal maps tile at a consistent texel density.
 */
export function smoothTube(mb: MeshBuilder, rings: TubeRing[], sides: number, opts: TubeOptions): void {
  const n = rings.length;
  if (n < 2) return;
  const jitter = opts.jitter ?? 0.06;
  const seed = opts.seed ?? 0;
  const dens = opts.uvDensity ?? 1;
  const avgR = rings.reduce((s, r) => s + r.r, 0) / n;
  const uRepeat = Math.max(1, Math.round(2 * Math.PI * avgR * dens));
  const tangents: Vector3[] = [];
  for (let i = 0; i < n; i++) {
    const a = rings[Math.max(i - 1, 0)]!.p;
    const b = rings[Math.min(i + 1, n - 1)]!.p;
    tangents.push(new Vector3().subVectors(b, a).normalize());
  }
  // Parallel transport.
  let normal = Math.abs(tangents[0]!.dot(_up)) > 0.9 ? new Vector3(1, 0, 0) : _up.clone();
  normal = new Vector3().crossVectors(tangents[0]!, normal).cross(tangents[0]!).normalize();
  const frames: { t: Vector3; n: Vector3; b: Vector3 }[] = [];
  for (let i = 0; i < n; i++) {
    const t = tangents[i]!;
    if (i > 0) {
      const prev = tangents[i - 1]!;
      const axis = new Vector3().crossVectors(prev, t);
      const s = axis.length();
      if (s > 1e-6) {
        axis.divideScalar(s);
        const ang = Math.acos(Math.min(1, Math.max(-1, prev.dot(t))));
        normal.applyAxisAngle(axis, ang);
      }
    }
    normal.sub(t.clone().multiplyScalar(normal.dot(t))).normalize();
    frames.push({ t: t.clone(), n: normal.clone(), b: new Vector3().crossVectors(t, normal).normalize() });
  }
  let len = 0;
  const startIdx = mb.vertexCount;
  const pos = new Vector3();
  const nor = new Vector3();
  for (let i = 0; i < n; i++) {
    const ring = rings[i]!;
    if (i > 0) len += ring.p.distanceTo(rings[i - 1]!.p);
    const f = frames[i]!;
    const dr = (rings[Math.min(i + 1, n - 1)]!.r - rings[Math.max(i - 1, 0)]!.r) / Math.max(1e-4, rings[Math.min(i + 1, n - 1)]!.p.distanceTo(rings[Math.max(i - 1, 0)]!.p));
    const tcol = i / (n - 1);
    let colr = opts.colorEnd ? mixRGB(opts.color, opts.colorEnd, tcol) : opts.color;
    if (opts.rootAO) colr = scaleRGB(colr, 1 - opts.rootAO * Math.max(0, 1 - len / 1.6));
    for (let s = 0; s <= sides; s++) {
      const a = (s / sides) * Math.PI * 2;
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      let rr = ring.r * (1 + (noise3(ca * 1.3 + seed, sa * 1.3, len * 0.9, seed) - 0.5) * 2 * jitter);
      let ribN = 0;
      if (opts.ribs) {
        const rib = Math.cos(a * opts.ribs);
        rr *= 1 + rib * (opts.ribDepth ?? 0.08);
        ribN = -Math.sin(a * opts.ribs) * (opts.ribDepth ?? 0.08) * opts.ribs;
      }
      // Radial direction and tangential direction around the ring.
      const radial = new Vector3().copy(f.n).multiplyScalar(ca).addScaledVector(f.b, sa);
      const around = new Vector3().copy(f.n).multiplyScalar(-sa).addScaledVector(f.b, ca);
      pos.copy(ring.p).addScaledVector(radial, rr);
      nor.copy(radial).addScaledVector(around, ribN * 0.6).addScaledVector(f.t, -dr).normalize();
      mb.vertex(pos, nor, colr, (s / sides) * uRepeat, len * dens);
    }
  }
  const stride = sides + 1;
  for (let i = 0; i < n - 1; i++) {
    for (let s = 0; s < sides; s++) {
      const a = startIdx + i * stride + s;
      const b = a + 1;
      const c = a + stride;
      const d = c + 1;
      mb.tri(a, b, c);
      mb.tri(b, d, c);
    }
  }
  const cap = (i: number, flip: boolean): void => {
    const ring = rings[i]!;
    const f = frames[i]!;
    const nn = f.t.clone().multiplyScalar(flip ? 1 : -1);
    const colr = opts.colorEnd && i > 0 ? opts.colorEnd : opts.color;
    const center = mb.vertex(ring.p.clone().addScaledVector(f.t, flip ? ring.r * (opts.capLift ?? 0.35) : 0), nn, colr, 0.5, 0.5);
    const base = mb.vertexCount;
    for (let s = 0; s < sides; s++) {
      const v = startIdx + i * stride + s;
      pos.set(mb.pos[v * 3]!, mb.pos[v * 3 + 1]!, mb.pos[v * 3 + 2]!);
      // Rounded cap: blend the ring normal toward the tube axis.
      nor.set(mb.nor[v * 3]!, mb.nor[v * 3 + 1]!, mb.nor[v * 3 + 2]!).lerp(nn, 0.6).normalize();
      mb.vertex(pos, nor, colr, 0.5 + Math.cos((s / sides) * Math.PI * 2) * 0.5, 0.5 + Math.sin((s / sides) * Math.PI * 2) * 0.5);
    }
    for (let s = 0; s < sides; s++) {
      const a = base + s;
      const b = base + ((s + 1) % sides);
      if (flip) mb.tri(center, a, b);
      else mb.tri(center, b, a);
    }
  };
  if (opts.capEnd) cap(n - 1, true);
  if (opts.capStart) cap(0, false);
}

/** Catmull-Rom resample of a polyline for smooth tube spines. */
export function smoothSpine(points: Vector3[], perSegment: number): Vector3[] {
  if (points.length < 3) return points.map((p) => p.clone());
  const out: Vector3[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(i - 1, 0)]!;
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    const p3 = points[Math.min(i + 2, points.length - 1)]!;
    for (let k = 0; k < perSegment; k++) {
      const t = k / perSegment;
      const t2 = t * t;
      const t3 = t2 * t;
      out.push(
        new Vector3(
          0.5 * (2 * p1.x + (-p0.x + p2.x) * t + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
          0.5 * (2 * p1.y + (-p0.y + p2.y) * t + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
          0.5 * (2 * p1.z + (-p0.z + p2.z) * t + (2 * p0.z - 5 * p1.z + 4 * p2.z - p3.z) * t2 + (-p0.z + 3 * p1.z - 3 * p2.z + p3.z) * t3),
        ),
      );
    }
  }
  out.push(points[points.length - 1]!.clone());
  return out;
}

// ------------------------------------------------------------------------------------------ leaf cards

/** A volume of billboard leaf cards (see foliage material). */
export interface Clump {
  center: Vector3;
  radius: Vector3;
  count: number;
  cardSize: number;
}

/**
 * Dense billboard leaf cards. Cards are placed mostly near the clump surface (full silhouettes,
 * little hidden overdraw) with a sparse interior so gaps never show the sky straight through.
 * Normals are spherical from the clump/tree centre for soft volumetric shading; aCard.w is a baked
 * occlusion term (deep interior & underside darker).
 */
export function cardCloud(clumps: Clump[], rng: Rng, opts: { treeCenter?: Vector3; aoFloor?: number; treeRadius?: number } = {}): BufferGeometry {
  let total = 0;
  for (const c of clumps) total += c.count;
  const pos = new Float32Array(total * 4 * 3);
  const nor = new Float32Array(total * 4 * 3);
  const corner = new Float32Array(total * 4 * 2);
  const card = new Float32Array(total * 4 * 4);
  const index = new Uint32Array(total * 6);
  const corners = [-1, -1, 1, -1, 1, 1, -1, 1];
  const n = new Vector3();
  const p = new Vector3();
  const sn = new Vector3();
  const tc = opts.treeCenter;
  const tr = opts.treeRadius ?? 3;
  const aoFloor = opts.aoFloor ?? 0.25;
  let k = 0;
  for (const c of clumps) {
    for (let i = 0; i < c.count; i++) {
      n.set(rng.float() * 2 - 1, rng.float() * 2 - 1, rng.float() * 2 - 1);
      if (n.lengthSq() < 1e-4) n.set(0, 1, 0);
      n.normalize();
      // Surface-biased radius with a lumpy outline.
      const lump = 0.85 + 0.3 * noise3(n.x * 2.2 + c.center.x, n.y * 2.2 + c.center.y, n.z * 2.2, 5);
      const r = (0.55 + 0.45 * Math.pow(rng.float(), 0.45)) * lump;
      p.set(n.x * c.radius.x * r, n.y * c.radius.y * r, n.z * c.radius.z * r).add(c.center);
      sn.copy(n);
      let depth01 = r;
      if (tc) {
        const d = p.clone().sub(tc);
        sn.lerp(d.clone().normalize(), 0.78).normalize();
        depth01 = Math.min(1, Math.max(r * 0.7, d.length() / tr));
      }
      // AO: interior and underside darker, top/outside open.
      const ao = Math.max(aoFloor, Math.min(1, depth01 * 0.7 + (sn.y * 0.5 + 0.5) * 0.35));
      const size = c.cardSize * (0.8 + rng.float() * 0.4);
      const rot = rng.float() * Math.PI * 2;
      const rand = rng.float();
      for (let v = 0; v < 4; v++) {
        const vi = k * 4 + v;
        pos[vi * 3] = p.x;
        pos[vi * 3 + 1] = p.y;
        pos[vi * 3 + 2] = p.z;
        nor[vi * 3] = sn.x;
        nor[vi * 3 + 1] = sn.y;
        nor[vi * 3 + 2] = sn.z;
        corner[vi * 2] = corners[v * 2]!;
        corner[vi * 2 + 1] = corners[v * 2 + 1]!;
        card[vi * 4] = size;
        card[vi * 4 + 1] = rot;
        card[vi * 4 + 2] = rand;
        card[vi * 4 + 3] = ao;
      }
      index.set([k * 4, k * 4 + 1, k * 4 + 2, k * 4, k * 4 + 2, k * 4 + 3], k * 6);
      k++;
    }
  }
  const g = new BufferGeometry();
  g.setAttribute('position', new BufferAttribute(pos, 3));
  g.setAttribute('normal', new BufferAttribute(nor, 3));
  g.setAttribute('aCorner', new BufferAttribute(corner, 2));
  g.setAttribute('aCard', new BufferAttribute(card, 4));
  g.setIndex(new BufferAttribute(index, 1));
  g.computeBoundingSphere();
  if (g.boundingSphere) g.boundingSphere.radius += 1;
  return g;
}

/** Concatenate card clouds (same attribute layout). */
export function mergeCardClouds(geos: BufferGeometry[]): BufferGeometry {
  const names = ['position', 'normal', 'aCorner', 'aCard'];
  let verts = 0;
  let idx = 0;
  for (const g of geos) {
    verts += g.getAttribute('position').count;
    idx += g.index!.count;
  }
  const out = new BufferGeometry();
  for (const name of names) {
    const size = geos[0]!.getAttribute(name).itemSize;
    const arr = new Float32Array(verts * size);
    let o = 0;
    for (const g of geos) {
      const a = g.getAttribute(name).array as Float32Array;
      arr.set(a, o);
      o += a.length;
    }
    out.setAttribute(name, new BufferAttribute(arr, size));
  }
  const index = new Uint32Array(idx);
  let vo = 0;
  let io = 0;
  for (const g of geos) {
    const ia = g.index!.array;
    for (let i = 0; i < ia.length; i++) index[io + i] = ia[i]! + vo;
    io += ia.length;
    vo += g.getAttribute('position').count;
  }
  out.setIndex(new BufferAttribute(index, 1));
  out.computeBoundingSphere();
  if (out.boundingSphere) out.boundingSphere.radius += 1;
  return out;
}

/**
 * Low-detail version of a card cloud: keeps every `keep`-th card and enlarges it so the canopy
 * keeps its coverage at a fraction of the vertex/fill cost.
 */
export function thinCards(g: BufferGeometry, keep: number, grow: number): BufferGeometry {
  const cards = g.getAttribute('position').count / 4;
  const kept: number[] = [];
  for (let i = 0; i < cards; i += keep) kept.push(i);
  const out = new BufferGeometry();
  for (const name of ['position', 'normal', 'aCorner', 'aCard']) {
    const src = g.getAttribute(name);
    const size = src.itemSize;
    const arr = new Float32Array(kept.length * 4 * size);
    kept.forEach((ci, k) => arr.set((src.array as Float32Array).subarray(ci * 4 * size, ci * 4 * size + 4 * size), k * 4 * size));
    if (name === 'aCard') for (let v = 0; v < kept.length * 4; v++) arr[v * 4] = arr[v * 4]! * grow;
    out.setAttribute(name, new BufferAttribute(arr, size));
  }
  const index = new Uint32Array(kept.length * 6);
  for (let k = 0; k < kept.length; k++) index.set([k * 4, k * 4 + 1, k * 4 + 2, k * 4, k * 4 + 2, k * 4 + 3], k * 6);
  out.setIndex(new BufferAttribute(index, 1));
  out.computeBoundingSphere();
  if (out.boundingSphere) out.boundingSphere.radius += 1;
  return out;
}

/** Translate/scale a card cloud in place — cards keep their screen-facing behaviour. */
export function transformCards(g: BufferGeometry, offset: Vector3, scale: number): BufferGeometry {
  const p = g.getAttribute('position');
  for (let i = 0; i < p.count; i++) p.setXYZ(i, p.getX(i) * scale + offset.x, p.getY(i) * scale + offset.y, p.getZ(i) * scale + offset.z);
  const c = g.getAttribute('aCard');
  for (let i = 0; i < c.count; i++) c.setX(i, c.getX(i) * scale);
  g.computeBoundingSphere();
  if (g.boundingSphere) g.boundingSphere.radius += scale;
  return g;
}
